import { singleton } from '#shared/singleton';
import type { User } from '#shared/data/Account';
import type { UserRoleEnum } from '#shared/enums';
import { ref } from 'vue';

/**
 * Manage the authentication state of the user.
 */
export const useAuth = singleton(() => {
    let isInitialized = false;
    
    // Reactive user data
    const userData = ref({
        id: 0,
        firstname: '',
        lastname: '',
        language: '',
        role: null as UserRoleEnum | null
    });

    // Track if we're hydrated on the client
    const isClientHydrated = ref(false);
    
    const initializeFromStorage = () => {
        if (!isInitialized && typeof window !== 'undefined') {
            userData.value = {
                id: Number(sessionStorage.getItem('id')) || 0,
                firstname: sessionStorage.getItem('firstname') || '',
                lastname: sessionStorage.getItem('lastname') || '',
                language: sessionStorage.getItem('language') || '',
                role: sessionStorage.getItem('role') as UserRoleEnum || null
            };
            isInitialized = true;
            isClientHydrated.value = true;
            
            // Resume session refresh timer if user is authenticated
            if (userData.value.id > 0) {
                const sessionRefresh = useSessionRefresh();
                sessionRefresh.resumeRefreshTimer();
            }
        }
    };

    const isAuthenticated = () => {
        // CRITICAL: Prevent hydration mismatches by ensuring server and client render identical initial state
        // Server has no sessionStorage, so must return false. Client must also return false initially
        // to match server-rendered DOM, then update after hydration completes.
        // Without this guard: Server renders "login form" → Client expects "welcome back" → Hydration error
        if (import.meta.server || !isClientHydrated.value) {
            return false;
        }
        
        // Initialize from storage if needed
        if (!isInitialized) {
            initializeFromStorage();
        }
        
        return userData.value.id > 0;
    }

    const setUserData = (user: User) => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('id', user.id?.toString() || '0');
            sessionStorage.setItem('firstname', user.firstname || '');
            sessionStorage.setItem('lastname', user.lastname || '');
            sessionStorage.setItem('language', user.language || '');
            sessionStorage.setItem('role', user.role);
        }
        
        // Update reactive data
        userData.value = {
            id: user.id || 0,
            firstname: user.firstname || '',
            lastname: user.lastname || '',
            language: user.language || '',
            role: user.role
        };
    };

    const getUserData = () => {
        return userData.value;
    };

    const clearUserData = () => {
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem('id');
            sessionStorage.removeItem('firstname');
            sessionStorage.removeItem('lastname');
            sessionStorage.removeItem('language');
            sessionStorage.removeItem('role');
        }
        
        // Clear reactive data
        userData.value = {
            id: 0,
            firstname: '',
            lastname: '',
            language: '',
            role: null
        };
    };

    const login = async (user: User) => {
        setUserData(user);

        // Start the automatic session refresh timer
        const sessionRefresh = useSessionRefresh();
        sessionRefresh.startRefreshTimer();

        const redirectPath = useRoute().query.redirect?.toString() || '/';
        const finalRedirectPath = redirectPath === '/' ? '/' : redirectPath;

        if (finalRedirectPath !== '/login') {
            return navigateTo({
                path: finalRedirectPath,
                replace: true
            });
        }
    }

    const logout = async () => {
        // Clear the session refresh timer before clearing user data
        const sessionRefresh = useSessionRefresh();
        sessionRefresh.clearRefreshTimer();
        
        clearUserData();
        
        const currentRoute = useRoute();
        navigateTo({
            path: '/login',
            query: { redirect: currentRoute.path !== '/login' ? currentRoute.path : undefined },
            replace: true
        });
    };

    const getRole = (): UserRoleEnum | null => {
        // CRITICAL: Prevent hydration mismatches by ensuring server and client render identical initial state
        // Server has no sessionStorage, so must return null. Client must also return null initially
        // to match server-rendered DOM, then update after hydration completes.
        if (import.meta.server || !isClientHydrated.value) {
            return null;
        }
        
        // Ensure we initialize first
        if (!isInitialized) {
            initializeFromStorage();
        }
        return userData.value.role;
    };

  return {
    clearUserData,
    isAuthenticated,
    login,
    logout,
    getUserData,
    getRole,
    setUserData,
    userData,
    isClientHydrated,
    hydrate: initializeFromStorage
  };
});
